import { useEffect, useState } from 'react';

import { useSwiper } from 'swiper/react';

type AutoplayControllerProps = {
  isAutoplayEnabled: boolean;
  setToggleHandler: (handler: (() => void) | null) => void;
  setIsAutoplayRunning: (isRunning: boolean) => void;
};

const AutoplayController = ({ isAutoplayEnabled, setToggleHandler, setIsAutoplayRunning }: AutoplayControllerProps) => {
  const swiper = useSwiper();

  useEffect(() => {
    if (!isAutoplayEnabled || !swiper?.autoplay) {
      setToggleHandler(null);
      setIsAutoplayRunning(false);
      return;
    }

    setIsAutoplayRunning(swiper.autoplay.running);

    const onAutoplayStart = () => {
      setIsAutoplayRunning(true);
    };

    const onAutoplayStop = () => {
      setIsAutoplayRunning(false);
    };

    swiper.on('autoplayStart', onAutoplayStart);
    swiper.on('autoplayStop', onAutoplayStop);

    setToggleHandler(() => () => {
      if (!swiper.autoplay) {
        return;
      }

      if (swiper.autoplay.running) {
        swiper.autoplay.stop();
        return;
      }

      swiper.autoplay.start();
    });

    return () => {
      swiper.off('autoplayStart', onAutoplayStart);
      swiper.off('autoplayStop', onAutoplayStop);
      setToggleHandler(null);
    };
  }, [isAutoplayEnabled, setIsAutoplayRunning, setToggleHandler, swiper]);

  return null;
};

const useAutoplayController = (isAutoplayEnabled: boolean) => {
  const [isAutoplayRunning, setIsAutoplayRunning] = useState(isAutoplayEnabled);
  const [toggleAutoplayHandler, setToggleAutoplayHandler] = useState<(() => void) | null>(null);

  useEffect(() => {
    setIsAutoplayRunning(isAutoplayEnabled);
  }, [isAutoplayEnabled]);

  return {
    isAutoplayRunning,
    toggleAutoplayHandler,
    setIsAutoplayRunning,
    setToggleAutoplayHandler,
    AutoplayController,
  };
};

export default useAutoplayController;
